import { useMsal } from '@azure/msal-react';
import DateFnsUtils from '@date-io/date-fns';
import {
    Button, Checkbox, CircularProgress, Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    Paper,
    TextField,
    Typography
} from '@material-ui/core';
import { CheckBox, CheckBoxOutlineBlank } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { de } from "date-fns/locale";
import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { globalStyles } from '../../../assets/globalStyles';
import { selectDate } from "../../../features/konfiguration/konfigurationSlice";
import { selectMessstellenLookup, selectMessstellenLoadingLookup } from '../../../features/messstellenLookup/messstellenLookupSlice';
import { validate, validateHelper } from '../../../features/validate/validate';
import { isAuthorized, rollen } from '../../../utils/auth/authConfig';
import { addTimezoneOffset, dateDiffInDaysUTC, subtractTimezoneOffset } from '../../../utils/utils';
import { freischaltung, messstelle } from './../../../utils/apiTypes';
import {
    resetViewMode, createNetzereignis, deleteNetzereignis, discardChanges, discardNew, getFreischaltungen, getMessstellen, resetError,
    selectAktuellesNetzereignis, selectEditDirty, selectEditEntry, selectError, selectFreischaltungen, selectFreischaltungLoading, selectMessstellen, selectMessstellenLoading, selectNetzereignisLoading, selectNewEntry, selectViewMode, setEditEntry, setEndDatum, setEreignistext, setEreignisueberschrift, setError, setFreischaltung, setGeplant, setGrund, setKommentar, setMessstelle, setStartDatum, updateNetzereignis
} from './netzereignisseSlice';
import { useStyles } from './netzereignisseStyles';

export const NetzereignisseEdit = (): JSX.Element => {

    const dispatch = useAppDispatch();
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
    const { instance } = useMsal();

    useEffect(() => {
        dispatch(getMessstellen());
        dispatch(getFreischaltungen());
    }, [instance]);

    const classes = {
        ...useStyles(),
        ...globalStyles(),
    };

    const
        aktuellesNetzereignis = useAppSelector(selectAktuellesNetzereignis),
        editEntry: boolean = useAppSelector(selectEditEntry),
        newEntry: boolean = useAppSelector(selectNewEntry),
        viewMode: boolean = useAppSelector(selectViewMode),
        dirtyEdit: number = useAppSelector(selectEditDirty),
        inputError: boolean = useAppSelector(selectError),
        messstellen: Array<messstelle> = useAppSelector(selectMessstellen),
        freischaltungen: Array<freischaltung> = useAppSelector(selectFreischaltungen),
        netzereignisLoading = useAppSelector(selectNetzereignisLoading),
        messstellenLoading = useAppSelector(selectMessstellenLoading),
        freischaltungLoading = useAppSelector(selectFreischaltungLoading),

        messstellenLookup = useAppSelector(selectMessstellenLookup),
        messstellenLoadingLookup = useAppSelector(selectMessstellenLoadingLookup);


    const isUTC = useAppSelector(selectDate);



    function printSaveDiscardButtons() {
        return (<div>
            <Button
                variant="contained"
                onClick={() => { newEntry ? dispatch(createNetzereignis(aktuellesNetzereignis)) : dispatch(updateNetzereignis(aktuellesNetzereignis)) }}
                color="secondary"
                className={classes.showtriButton}
                disabled={aktuellesNetzereignis.ereignisueberschrift === ""
                    || aktuellesNetzereignis.messstelle === ""
                    || aktuellesNetzereignis.ereignistext === ""
                    || aktuellesNetzereignis.grund === ""
                    || !dirtyEdit
                    || inputError
                }
                style={{ minHeight: "36px" }}
            >
                {netzereignisLoading ? <CircularProgress size="20px" style={{ padding: "1px" }} /> : "Speichern"}
            </Button>
            <Button
                variant="contained"
                onClick={() => { newEntry ? dispatch(discardNew()) : dispatch(discardChanges()) }}
                color="secondary"
                className={classes.showtriButton}
            >
                Abbrechen
            </Button>
        </div>)
    }
    function confirmDeleteOfNetzereignis() {
        dispatch(deleteNetzereignis(aktuellesNetzereignis));
        setOpenDeleteDialog(false);
    }

    function printEditDeleteButton() {
        if (isAuthorized([rollen.admin, rollen.betrieb, rollen.technik, rollen.netzwirtschaft]))
            return (
                <div>

                    <Button
                        variant="contained"
                        onClick={() => dispatch(setEditEntry())}
                        color="secondary"
                        className={classes.showtriButton}
                    >
                        Bearbeiten
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setOpenDeleteDialog(true)}
                        color="secondary"
                        className={`${classes.backgroundColorErrorDark} ${classes.showtriButton}`}
                    >
                        Löschen
                    </Button>

                    <Dialog
                        open={openDeleteDialog}
                        onClose={() => setOpenDeleteDialog(false)}
                    >
                        <DialogTitle>Netzereignis löschen</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {'Sind Sie sicher, dass Sie das Netzereignis "' +
                                    aktuellesNetzereignis.ereignisueberschrift +
                                    '" wirklich löschen wollen?'}
                            </DialogContentText>
                        </DialogContent>

                        <DialogActions>
                            <Button
                                color="secondary"
                                variant="contained"
                                onClick={() => setOpenDeleteDialog(false)}
                                className={classes.cancleDeleteButton}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                variant="contained"
                                onClick={confirmDeleteOfNetzereignis}
                                className={classes.confirmDeleteButton}
                                style={{ minWidth: "120px", minHeight: "36px" }}
                            >
                                {netzereignisLoading ? <CircularProgress size="20px" style={{ color: "black" }} /> : "Löschen"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Button
                        variant="contained"
                        onClick={() => { dispatch(resetViewMode())}}
                        color="default"
                        className={classes.showtriButton}
                    >
                        Schließen
                    </Button>
                </div>
            );
        else return (<React.Fragment />)
    }

    const [messstelleInputValue, setMessstelleInputValue] = React.useState('');
    const [freischaltungInputValue, setFreischaltungInputValue] = React.useState('');
    const [geplantSelectInputValue, setGeplantSelectInputValue] = React.useState('');

    const translateGeplantBoolToString = (geplant: boolean) => {
        return geplant ? "Geplant" : "Ungeplant";
    }

    if (viewMode)
        return (<Container maxWidth={false} className={classes.root}>
            <Paper className={classes.paper} >
                <form className={classes.root} noValidate autoComplete="off" style={{ marginTop: "10px", marginLeft: "24px", marginRight: "24px", marginBottom: "10px" }}>
                    <div>
                        <Grid container spacing={4} >
                            <Grid item container xs={12}>
                                <Grid item md={3}>
                                <Typography component={"div"} variant="h6" color="primary" className={classes.typoH6Style}>
                                    {newEntry ? "Neues Netzereignis anlegen" : "Bearbeiten von Netzereignis: " + aktuellesNetzereignis.ereignisueberschrift}
                                </Typography>
                            </Grid>
                                <Grid item md={3} />
                                    <Grid item md={6}>
                                    {editEntry ? printSaveDiscardButtons() : printEditDeleteButton()}
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl className={classes.fullWidth}>
                                    <TextField
                                        required={true}
                                        id="netzereignis-Ereignisueberschrift" disabled={editEntry ? false : true}
                                        label="Ereignisüberschrift" variant="outlined"
                                        error={validate(dispatch, setError, resetError, aktuellesNetzereignis.ereignisueberschrift, "ereignisüberschrift")}
                                        helperText={validateHelper(aktuellesNetzereignis.ereignisueberschrift)}
                                        inputProps={{ maxLength: 100 }}
                                        onChange={(event) => dispatch(setEreignisueberschrift(event.target.value))} value={aktuellesNetzereignis.ereignisueberschrift} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl className={classes.fullWidth}>
                                    <Autocomplete
                                        id="select-messstelle"
                                        noOptionsText={<React.Fragment>
                                            {messstellenLoading && messstellenLoadingLookup ? <CircularProgress style={{ padding: "5px" }} /> : "Keine Messstelle"}
                                        </React.Fragment>}
                                        disabled={!editEntry}
                                        value={aktuellesNetzereignis.messstelle}
                                        onChange={(_, newValue) => {
                                            if (newValue) dispatch(setMessstelle(newValue));
                                        }}
                                        inputValue={messstelleInputValue}
                                        onInputChange={(_, newInputValue) => {
                                            setMessstelleInputValue(newInputValue);
                                        }}
                                        options={
                                            messstellen.map((x: messstelle) => x.name )}
                                        getOptionLabel={(option) =>{ 
                                            const currentMessstelle = messstellenLookup.find(x => x.name == option)
                                            if (currentMessstelle)
                                                return currentMessstelle.umspannwerk + ": " + currentMessstelle.schaltfeld + " (" + currentMessstelle.name + ")";
                                            return "";
                                        }}
                                        renderInput={(params) => <TextField
                                            required={true}
                                            {...params}
                                            label="Messstelle"
                                            variant="outlined"
                                        />}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl className={classes.fullWidth}>
                                    <TextField
                                        id="netzereignis-grund" disabled={editEntry ? false : true}
                                        label="Grund der Anforderung" variant="outlined"
                                        required={true}
                                        error={validate(dispatch, setError, resetError, aktuellesNetzereignis.grund, "grund")}
                                        helperText={validateHelper(aktuellesNetzereignis.grund)}
                                        inputProps={{ maxLength: 100 }}
                                        onChange={(event) => dispatch(setGrund(event.target.value))} value={aktuellesNetzereignis.grund} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl className={classes.fullWidth}>
                                    <Autocomplete
                                        id="select-freischaltung"
                                        noOptionsText={<React.Fragment>
                                            {freischaltungLoading ? <CircularProgress style={{ padding: "5px" }} /> : "Keine Freischaltungen"}
                                        </React.Fragment>}
                                        multiple
                                        disableCloseOnSelect
                                        disabled={editEntry ? false : true}
                                        value={aktuellesNetzereignis.freischaltung}
                                        onChange={(_, newValue) => {
                                            if (newValue) dispatch(setFreischaltung(newValue));
                                        }}
                                        inputValue={freischaltungInputValue}
                                        onInputChange={(_, newInputValue) => {
                                            setFreischaltungInputValue(newInputValue);
                                        }}
                                        options={
                                            freischaltungen.map((x: freischaltung) => x.name)}
                                        getOptionLabel={(option) => option ? option : ""}
                                        renderInput={(params) => <TextField {...params}
                                            label="Freischaltung"
                                            variant="outlined"
                                        />}
                                        renderOption={(option, { selected }) => (
                                            <React.Fragment>
                                                <Checkbox
                                                    icon={
                                                        <CheckBoxOutlineBlank fontSize="small" />
                                                    }
                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {option}
                                            </React.Fragment>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl className={classes.fullWidth}>
                                    <Autocomplete
                                        id="select-geplant"
                                        noOptionsText={"Keine Optionen"}
                                        disabled={editEntry ? false : true}
                                        value={translateGeplantBoolToString(aktuellesNetzereignis.geplant)}
                                        onChange={(_, newValue) => {
                                            if (newValue) dispatch(setGeplant(newValue));
                                        }}
                                        inputValue={geplantSelectInputValue}
                                        onInputChange={(_, newInputValue) => {
                                            setGeplantSelectInputValue(newInputValue);
                                        }}
                                        options={["Geplant", "Ungeplant"]}
                                        getOptionLabel={(option) => option ? option : ""}
                                        renderInput={(params) => <TextField {...params} label="Geplant/Ungeplant" variant="outlined" />}

                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl className={classes.fullWidth}>
                                    <MuiPickersUtilsProvider locale={de} utils={DateFnsUtils}>
                                        <KeyboardDateTimePicker
                                            id="netzereignis-startzeit"
                                            format="dd.MM.yyyy HH:mm:ss"
                                            okLabel="OK"
                                            cancelLabel="ABBRECHEN"
                                            disabled={editEntry ? false : true}
                                            label="Startzeit"
                                            inputVariant="outlined"
                                            ampm={false}
                                            invalidDateMessage={"Das ausgewählte Datum hat ein ungültiges Format."}
                                            value={isUTC ? addTimezoneOffset(new Date(aktuellesNetzereignis.zeit)) : aktuellesNetzereignis.zeit}
                                            onChange={(event) => dispatch(setStartDatum(isUTC ? subtractTimezoneOffset(new Date(event + "")) : event))}
                                        />
                                    </MuiPickersUtilsProvider>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl className={classes.fullWidth}>
                                    <MuiPickersUtilsProvider locale={de} utils={DateFnsUtils}>
                                        <KeyboardDateTimePicker
                                            id="netzereignis-endzeit"
                                            okLabel="OK"
                                            cancelLabel="ABBRECHEN"
                                            format="dd.MM.yyyy HH:mm:ss"
                                            disabled={editEntry ? false : true}
                                            label="Endzeit"
                                            inputVariant="outlined"
                                            ampm={false}
                                            minDate={aktuellesNetzereignis.zeit}
                                            minDateMessage={"Das ausgewählte Datum liegt vor der Startzeit."}
                                            invalidDateMessage={"Das ausgewählte Datum hat ein ungültiges Format."}
                                            value={isUTC ? addTimezoneOffset(new Date(aktuellesNetzereignis.ende)) : aktuellesNetzereignis.ende}
                                            onChange={(event) => dispatch(setEndDatum(isUTC ? subtractTimezoneOffset(new Date(event + "")) : event))}
                                        />
                                    </MuiPickersUtilsProvider>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl className={classes.fullWidth}>
                                    <TextField
                                        id="netzereignis-anzahltage"
                                        disabled={true}
                                        label="Anzahl Tage" variant="outlined"
                                        inputProps={{ maxLength: 100 }}
                                        value={dateDiffInDaysUTC(new Date(aktuellesNetzereignis.zeit), new Date(aktuellesNetzereignis.ende))}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl className={classes.fullWidth}>
                                    <TextField
                                        required={true}
                                        id="netzereignis-ereignistext" disabled={editEntry ? false : true}
                                        label="Ereignistext" variant="outlined"
                                        multiline
                                        rows={5}
                                        rowsMax={5}
                                        onChange={(event) => dispatch(setEreignistext(event.target.value))} value={aktuellesNetzereignis.ereignistext} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl className={classes.fullWidth}>
                                    <TextField id="netzereignis-kommentar" disabled={editEntry ? false : true}
                                        label="Kommentar" variant="outlined"
                                        multiline
                                        rows={5}
                                        rowsMax={5}
                                        onChange={(event) => dispatch(setKommentar(event.target.value))} value={aktuellesNetzereignis.kommentar} />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </div>
                </form>
            </Paper>
        </Container>)
    else return (<React.Fragment />)

}
